import {
    SelectChangeEvent,
    Box,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    FormControlLabel,
    Checkbox,
    Button
} from '@mui/material';
import { ChangeEvent, MouseEventHandler, useEffect, useState } from 'react';
import { useEventContext, useUserContext } from 'contexts';
import { QuizTypes } from 'consts';
import { Quiz } from 'types';

interface Props {
    initQuiz?: Quiz;
    onSaveQuiz: (quiz: Quiz) => void;
    canDeleteQuiz?: boolean;
    deletingQuiz?: boolean;
    onDeleteQuiz?: MouseEventHandler<HTMLButtonElement>;
}

export const EditQuiz = (props: Props) => {
    const user = useUserContext();
    const event = useEventContext();
    const [quiz, setQuiz] = useState<Quiz>({
        authorUserId: user.id,
        description: '',
        name: '',
        publiclyAvailable: false,
        quizTypeId: QuizTypes.Slideshow,
        eventId: event?.id
    });

    useEffect(() => {
        if (props.initQuiz) {
            setQuiz(props.initQuiz);
        }
    }, [props.initQuiz]);

    const onSaveQuiz = async () => {
        props.onSaveQuiz(quiz);
    };

    const onGenreChange = (event: SelectChangeEvent<number>) => {
        setQuiz({
            ...quiz,
            genreId: event.target.value as number
        });
    };

    const onQuizTypeChange = (event: SelectChangeEvent<number>) => {
        setQuiz({
            ...quiz,
            quizTypeId: event.target.value as number
        });
    };

    const onPubliclyAvailableChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setQuiz({
            ...quiz,
            publiclyAvailable: checked
        });
    };

    const onTextChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const fieldName = event.target.getAttribute('data-field') as keyof Quiz;

        setQuiz({
            ...quiz,
            [fieldName]: event.target.value as string
        });
    };

    const isInputValid = (): boolean => {
        if (!quiz.name || !quiz.description || !quiz.quizTypeId) {
            return false;
        }
        return true;
    };

    return (
        <Box maxWidth={'80%'} sx={{display: 'flex', alignItems: 'center', margin: '0 20px', flexGrow: 1}}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        id="txt-name"
                        label="Name"
                        fullWidth
                        onChange={onTextChange}
                        value={quiz.name ?? ''}
                        inputProps={{ 'data-field': 'name' }}
                        InputLabelProps={{ shrink: !!quiz.name }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        id="txt-description"
                        label="Description"
                        multiline
                        fullWidth
                        rows={4}
                        onChange={onTextChange}
                        value={quiz.description ?? ''}
                        inputProps={{ 'data-field': 'description' }}
                        InputLabelProps={{ shrink: !!quiz.description }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <InputLabel id="lbl-quiz-type">Quiz Type</InputLabel>
                        <Select
                            labelId="lbl-quiz-type"
                            id="ddl-quiz-type"
                            value={quiz.quizTypeId}
                            label="Quiz Type"
                            autoWidth
                            onChange={onQuizTypeChange}
                        >
                            <MenuItem value={QuizTypes.Slideshow}>{QuizTypes[QuizTypes.Slideshow]}</MenuItem>
                            <MenuItem value={QuizTypes.Battleship}>{QuizTypes[QuizTypes.Battleship]}</MenuItem>
                            <MenuItem value={QuizTypes.Jeopardy}>{QuizTypes[QuizTypes.Jeopardy]}</MenuItem>
                            <MenuItem value={QuizTypes.Sharpshooter}>{QuizTypes[QuizTypes.Sharpshooter]}</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6}>
                    {/* Quiz genre */}
                    <FormControl fullWidth>
                        <InputLabel id="lbl-genre">Genre</InputLabel>
                        <Select
                            labelId="lbl-genre"
                            id="ddl-genre"
                            value={quiz.genreId ?? ""} // Note: MUI Select has an issue with undefined/null values so we need to set and empty string here when genre is not defined. https://github.com/mui/material-ui/issues/4991
                            label="Genre"
                            autoWidth
                            onChange={onGenreChange}

                        >
                            <MenuItem value={1}>SF</MenuItem>
                            <MenuItem value={2}>Fantasy</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                id="chb-publicly-available"
                                inputProps={{
                                    'aria-label': 'Is publicly available'
                                }}
                                checked={quiz.publiclyAvailable}
                                onChange={onPubliclyAvailableChange}
                            />
                        }
                        label="Is publicly available"
                    />
                </Grid>
                <Grid item xs={12} alignContent={'end'} sx={{ display: 'flex', gap: 2 }}>
                    <Button variant="contained" onClick={onSaveQuiz} disabled={!isInputValid() || props.deletingQuiz}>
                        Save quiz
                    </Button>
                    {props.canDeleteQuiz && props.onDeleteQuiz && (
                        <Button color="error" variant="outlined" onClick={props.onDeleteQuiz} disabled={props.deletingQuiz}>
                            Delete quiz
                        </Button>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};
